// AI Assistant Page - Magnus Resolve
// Matches Magnus PFMEA visual design with dark mode, cards, and clean layout
// ============================================================

import React, { useState, useRef, useEffect } from 'react';
import clsx from 'clsx';
import { useTheme } from '../contexts/ThemeContext';
import { useFracas } from '../contexts/FracasContext';
import { useEightD } from '../contexts/EightDContext';
import { useLabSample } from '../contexts/LabSampleContext';
import {
    AISuggestedPrompt,
    AICategoryType,
    getAISuggestedPrompts,
} from '../constants/aiAssistantPrompts';
import { Message } from '../components/ai/types';
import { AIChatMessageList } from '../components/ai/AIChatMessageList';
import { AIChatInput } from '../components/ai/AIChatInput';

// Refactored sub-components
import { AIDataContextSidebar } from '../components/ai/AIDataContextSidebar';
import { AIToolkitSidebar } from '../components/ai/AIToolkitSidebar';
import { AIHubHeader } from '../components/ai/AIHubHeader';
import { chatWithAI } from '../services/aiService';

export const AIAssistantPage: React.FC = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    // Data Hooks
    const { incidents } = useFracas();
    const { reports } = useEightD();
    const { samples } = useLabSample();

    const [messages, setMessages] = useState<Message[]>([
        {
            id: '1',
            role: 'ai',
            content: `Hello! I am your **Magnus Resolve AI Assistant**. I have connected to your **Live Data Context**:
            
📊 **FRACAS**: Monitoring ${incidents.length} incidents
🎯 **8D Reports**: Tracking ${reports.length} problem solving reports
🧪 **Lab Samples**: Analyzing ${samples.length} test samples

**Select a category on the right** or ask me directly about your data!`,
            timestamp: Date.now()
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSidebar, setShowSidebar] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<AICategoryType>('FRACAS Intelligence');
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const suggestedPrompts = getAISuggestedPrompts();
    const filteredPrompts = suggestedPrompts.filter(p => p.category === selectedCategory);

    // Active Data Summaries
    const activeIncidents = incidents.filter(i => i.status !== 'Closed');
    const criticalIncidents = incidents.filter(i => i.severity === 'Critical');
    const activeReports = reports.filter(r => r.status !== 'Closed');
    const activeSamples = samples.filter(s => s.status !== 'complete');
    const criticalSamplesCount = activeSamples.filter(s => s.priority === 'critical').length;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async (promptText?: string) => {
        const messageText = promptText || input;
        if (!messageText.trim() || isLoading) return;

        const userMessage: Message = {
            id: Date.now().toString(),
            role: 'user',
            content: messageText,
            timestamp: Date.now()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        try {
            const context = {
                incidents,
                reports,
                samples,
                pageName: 'AI Assistant Hub',
                activeProject: 'Magnus Resolve Global'
            };

            const response = await chatWithAI(messageText, context);

            const aiMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: response,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, aiMessage]);
        } catch (error: any) {
            const errorMessage: Message = {
                id: (Date.now() + 1).toString(),
                role: 'ai',
                content: `Error: ${error.message}. Please ensure the Magnus backend is running.`,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePromptClick = (prompt: AISuggestedPrompt) => {
        handleSend(prompt.prompt);
    };

    const clearChat = () => {
        setMessages([{
            id: Date.now().toString(),
            role: 'ai',
            content: 'Chat cleared! I am ready to analyze your reliability data.',
            timestamp: Date.now()
        }]);
    };

    const copyToClipboard = async (content: string, id: string) => {
        await navigator.clipboard.writeText(content);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    return (
        <div className="flex gap-4 h-[calc(100vh-8rem)]">
            <AIDataContextSidebar
                isDark={isDark}
                incidentsLength={incidents.length}
                activeIncidentsLength={activeIncidents.length}
                criticalIncidentsLength={criticalIncidents.length}
                reportsLength={reports.length}
                activeReportsLength={activeReports.length}
                samplesLength={samples.length}
                activeSamplesLength={activeSamples.length}
                criticalSamplesLength={criticalSamplesCount}
            />

            <div className="flex-1 flex flex-col">
                <AIHubHeader
                    isDark={isDark}
                    showSidebar={showSidebar}
                    setShowSidebar={setShowSidebar}
                    clearChat={clearChat}
                />

                <div className={clsx(
                    'flex-1 rounded-xl border overflow-hidden flex flex-col',
                    isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200 shadow-sm'
                )}>
                    <AIChatMessageList
                        messages={messages}
                        isLoading={isLoading}
                        messagesEndRef={messagesEndRef}
                        isDark={isDark}
                        copiedId={copiedId}
                        copyToClipboard={copyToClipboard}
                    />
                    <AIChatInput
                        input={input}
                        setInput={setInput}
                        handleSend={() => handleSend()}
                        isLoading={isLoading}
                        isDark={isDark}
                    />
                </div>
            </div>

            {showSidebar && (
                <AIToolkitSidebar
                    isDark={isDark}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    filteredPrompts={filteredPrompts}
                    handlePromptClick={handlePromptClick}
                    allPrompts={suggestedPrompts}
                />
            )}
        </div>
    );
};

export default AIAssistantPage;
